import { useState, useEffect, useCallback } from "react";
import { Loader2, ShieldCheck, Tag, Check, X } from "lucide-react";
import { getPaymentConfig, createPaymentOrder, verifyPayment, validatePromo } from "@/lib/api";
import { PACKAGES, inr } from "@/lib/packages";
import { toast } from "@/components/ui/sonner";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const loadScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const scrollToBooking = () => {
  const el = document.querySelector("#booking");
  if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -40 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

// Pay for the Precision Liver Evaluation via Razorpay, with optional promo code.
// Optional lead capture: `lead` ({name,email,phone,booking_id}) is attached to the
// order; `requireLead` blocks payment until name/email/phone are filled;
// `resolveLead` (async) runs at pay-time (e.g. create a booking first) — return
// null to abort. `onPaid` fires after successful verification.
export const PayButton = ({ className = "", variant = "light", showPromo = true, lead = null, requireLead = false, resolveLead = null, onPaid = null, tier = "basic" }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [promo, setPromo] = useState(null); // {code, discount, final_amount, label}

  useEffect(() => {
    getPaymentConfig().then(setConfig).catch(() => setConfig({ enabled: false }));
  }, []);

  const pkg = PACKAGES[tier] || PACKAGES.basic;
  const base = pkg.price;
  const price = promo ? promo.final_amount : base;

  const applyPromo = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const res = await validatePromo(code.trim(), tier);
      setPromo(res);
      toast.success(`Code applied — ${res.label}`);
    } catch (e) {
      setPromo(null);
      toast.error("Invalid or expired code");
    } finally {
      setApplying(false);
    }
  };

  const clearPromo = () => {
    setPromo(null);
    setCode("");
  };

  const handlePay = useCallback(async () => {
    if (config === null) return;
    if (!config?.enabled) {
      toast.info("Online payment is coming soon — reserve your slot and our team will confirm.");
      scrollToBooking();
      return;
    }
    setLoading(true);
    const ok = await loadScript();
    if (!ok) {
      toast.error("Couldn't load the payment window. Please try again.");
      setLoading(false);
      return;
    }
    try {
      let leadPayload = lead;
      if (resolveLead) {
        leadPayload = await resolveLead();
        if (!leadPayload) return; // validation/booking failed — resolver already toasted
      }
      if (requireLead && (!leadPayload?.name?.trim() || !leadPayload?.email?.trim() || !leadPayload?.phone?.trim())) {
        toast.error("Please fill in your name, email and phone above.");
        return;
      }
      const order = await createPaymentOrder({ promo_code: promo?.code || null, package: tier, ...(leadPayload || {}) });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "SuperAge Health",
        description: pkg.fullName,
        order_id: order.order_id,
        theme: { color: "#0A2E22" },
        prefill: leadPayload
          ? { name: leadPayload.name || "", email: leadPayload.email || "", contact: leadPayload.phone || "" }
          : undefined,
        handler: async (res) => {
          try {
            await verifyPayment({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — your assessment is reserved!");
            onPaid?.();
          } catch (e) {
            toast.error("Payment could not be verified. Please contact us.");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start payment. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [config, promo, lead, requireLead, resolveLead, onPaid, tier]);

  const styles =
    variant === "light"
      ? "bg-white text-sa-forest hover:scale-[1.03] shadow-[0_10px_30px_-8px_rgba(10,46,34,0.25)]"
      : "bg-sa-copper text-white hover:bg-sa-copper-deep hover:scale-[1.03]";

  // "light" variant sits on the dark forest section; "copper" sits on white
  // cards/modals — promo UI follows the surface.
  const onDark = variant === "light";

  return (
    <div className={className}>
      {showPromo && (
        <div className="mb-4">
          {promo ? (
            <div
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${
                onDark ? "bg-white/10 text-white" : "border border-sa-beige bg-sa-bg text-sa-forest"
              }`}
              data-testid="promo-applied"
            >
              <Tag className="h-3.5 w-3.5 text-sa-copper" />
              <span className="font-medium">{promo.code}</span>
              <span className={onDark ? "text-white/60" : "text-sa-muted"}>· {promo.label}</span>
              <button onClick={clearPromo} data-testid="promo-clear" aria-label="Remove code">
                <X className={`h-3.5 w-3.5 ${onDark ? "text-white/60 hover:text-white" : "text-sa-muted hover:text-sa-forest"}`} />
              </button>
            </div>
          ) : (
            <div className="flex max-w-xs items-center gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Promo code"
                data-testid="promo-input"
                className={`w-full rounded-full border px-4 py-2 text-sm outline-none focus:border-sa-copper ${
                  onDark
                    ? "border-white/20 bg-white/5 text-white placeholder:text-white/40"
                    : "border-sa-beige bg-sa-bg text-sa-forest placeholder:text-sa-muted"
                }`}
              />
              <button
                onClick={applyPromo}
                disabled={applying || !code.trim()}
                data-testid="promo-apply"
                className="inline-flex items-center gap-1.5 rounded-full bg-sa-copper px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sa-copper-deep disabled:opacity-50"
              >
                {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Apply
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-4">
        <button
          onClick={handlePay}
          disabled={loading || config === null}
          data-testid="pay-button"
          className={`group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[15px] font-medium tracking-tight transition-[transform,background-color] duration-300 active:scale-[0.98] disabled:opacity-70 ${styles}`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Pay {inr(price)} & Reserve
        </button>
        {promo && (
          <span className={`text-sm ${onDark ? "text-white/50" : "text-sa-muted"}`} data-testid="promo-price">
            <span className="line-through">{inr(base)}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default PayButton;
